import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { expenseService } from "@/services/expenseService";
import { Edit, Plus, Receipt, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const EXPENSE_CATEGORIES = [
  "Office Supplies",
  "Travel",
  "Utilities",
  "Marketing",
  "Software",
  "Equipment",
  "Rent",
  "Insurance",
  "Professional Services",
  "Other"
];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    category: "",
    amount: "",
    description: "",
    expenseDate: new Date().toISOString().split('T')[0],
    receiptUrl: ""
  });

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const { data, error } = await expenseService.getAll();
      if (error) throw error;

      const mappedExpenses = (data || []).map(exp => ({
        id: exp.id,
        category: exp.category,
        amount: exp.amount,
        description: exp.description,
        expenseDate: exp.expense_date,
        receiptUrl: exp.receipt_url,
        createdAt: exp.created_at
      }));

      setExpenses(mappedExpenses);
    } catch (error) {
      console.error("Fetch expenses error:", error);
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Map UI fields to DB fields
      const payload = {
        category: formData.category,
        amount: parseFloat(formData.amount),
        description: formData.description,
        expense_date: formData.expenseDate,
        receipt_url: formData.receiptUrl
      };

      if (editingExpense) {
        const { error } = await expenseService.update(editingExpense.id, payload);
        if (error) throw error;
        toast.success("Expense updated successfully");
      } else {
        const { error } = await expenseService.create(payload);
        if (error) throw error;
        toast.success("Expense added successfully");
      }

      setIsDialogOpen(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      console.error("Save expense error:", error);
      toast.error("Failed to save expense");
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      amount: expense.amount.toString(),
      description: expense.description,
      expenseDate: expense.expenseDate, // Already mapped in fetch
      receiptUrl: expense.receiptUrl || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const { error } = await expenseService.delete(id);
      if (error) throw error;
      toast.success("Expense deleted successfully");
      fetchExpenses();
    } catch (error) {
      console.error("Delete expense error:", error);
      toast.error("Failed to delete expense");
    }
  };

  const resetForm = () => {
    setFormData({
      category: "",
      amount: "",
      description: "",
      expenseDate: new Date().toISOString().split('T')[0],
      receiptUrl: ""
    });
    setEditingExpense(null);
  };

  const filteredExpenses = expenses.filter((expense) =>
    (expense.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (expense.category || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

  if (loading) {
    return <div className="skeleton h-96 rounded-2xl"></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="expenses-container">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-sky-700 to-sky-500 bg-clip-text text-transparent">Expenses</h1>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-sky-400 to-sky-600 hover:from-sky-500 hover:to-sky-700 transition-all duration-300" data-testid="add-expense-btn">
              <Plus size={20} className="mr-2" />
              Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="expense-dialog">
            <DialogHeader>
              <DialogTitle>{editingExpense ? "Edit Expense" : "Add New Expense"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                  <SelectTrigger className="mt-1" data-testid="expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                  className="mt-1"
                  data-testid="expense-amount-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="What was this expense for?"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  className="mt-1"
                  rows={3}
                  data-testid="expense-description-input"
                />
              </div>

              <div>
                <Label htmlFor="expenseDate">Date *</Label>
                <Input
                  id="expenseDate"
                  type="date"
                  value={formData.expenseDate}
                  onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="expense-date-input"
                />
              </div>

              <div>
                <Label htmlFor="receiptUrl">Receipt URL (optional)</Label>
                <Input
                  id="receiptUrl"
                  type="url"
                  placeholder="https://..."
                  value={formData.receiptUrl}
                  onChange={(e) => setFormData({ ...formData, receiptUrl: e.target.value })}
                  className="mt-1"
                  data-testid="expense-receipt-input"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-expense-btn">
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-expense-btn">
                  {editingExpense ? "Update" : "Add"} Expense
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Total */}
      <div className="glass rounded-2xl p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-expenses-input"
              data-value={searchTerm}
            />
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Expenses</p>
            <p className="text-2xl font-bold text-gray-800" data-testid="total-expenses-display">₹{totalExpenses.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="glass rounded-2xl p-6">
        {filteredExpenses.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 text-lg">No expenses found</p>
            <Button className="mt-4" variant="outline" onClick={() => setIsDialogOpen(true)}>
              Add Your First Expense
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredExpenses.map((expense) => (
              <div
                key={expense.id}
                className="flex items-center justify-between p-4 rounded-xl hover:bg-white/50"
                data-testid={`expense-item-${expense.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-700">
                      {expense.category}
                    </span>
                    <span className="text-sm text-gray-500">{expense.expenseDate}</span>
                  </div>
                  <p className="font-semibold text-gray-800">{expense.description}</p>
                </div>
                <div className="flex items-center gap-4">
                  <p className="text-xl font-bold text-gray-800">₹{parseFloat(expense.amount).toFixed(2)}</p>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(expense)} data-testid={`edit-expense-${expense.id}-btn`}>
                      <Edit size={16} className="text-blue-500" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`delete-expense-${expense.id}-trigger`}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Expense</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this expense? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(expense.id)} data-testid={`confirm-delete-expense-${expense.id}-btn`}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
